/**
 * NewCustomersPanel — Kunden, deren erste Bestellung im Zeitraum liegt.
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { ChevronDown, ChevronUp, UserPlus } from 'lucide-react';
import { Panel, PanelEmpty, PanelSkeleton } from './Panel';
import { eur, num } from './format';

const INITIAL = 6;

const initials = (name) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

export default function NewCustomersPanel({ loading, list, newContacts, onSelect }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? list : list.slice(0, INITIAL);

  return (
    <Panel
      overline="Erstbestellung im Zeitraum"
      title="Neue Kunden"
      meta={
        !loading && (
          <p className="text-xs text-slate-400 tabular-nums">
            <span className="font-display text-lg font-extrabold text-slate-900">{num(list.length)}</span>
            <span className="block">{num(newContacts)} neue Kontakte</span>
          </p>
        )
      }
    >
      {loading ? (
        <PanelSkeleton rows={4} />
      ) : list.length === 0 ? (
        <PanelEmpty
          icon={<UserPlus className="w-8 h-8" />}
          title="Keine Neukunden im Zeitraum"
          hint="Sobald ein Kontakt zum ersten Mal bestellt, erscheint er hier."
        />
      ) : (
        <>
          <ul className="divide-y divide-slate-100">
            {visible.map((c) => (
              <li key={c.key}>
                <button
                  type="button"
                  onClick={() => onSelect(c)}
                  className="w-full -mx-2 px-2 w-[calc(100%+1rem)] flex items-center gap-3 py-2.5 rounded-md text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
                >
                  <span className="w-9 h-9 shrink-0 rounded-lg bg-brand-50 text-brand-900 text-xs font-bold flex items-center justify-center">
                    {initials(c.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-900 truncate">{c.name}</span>
                    <span className="block text-[11px] text-slate-400 tabular-nums">
                      seit {format(c.firstDate, 'dd.MM.yyyy', { locale: de })}
                    </span>
                  </span>
                  <span className="text-sm font-semibold text-slate-900 tabular-nums">{eur(c.firstValue)}</span>
                </button>
              </li>
            ))}
          </ul>
          {list.length > INITIAL && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 w-full h-9 inline-flex items-center justify-center gap-1 rounded-md text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors"
            >
              {expanded
                ? <><ChevronUp className="w-4 h-4" /> Weniger anzeigen</>
                : <><ChevronDown className="w-4 h-4" /> Alle {list.length} anzeigen</>}
            </button>
          )}
        </>
      )}
    </Panel>
  );
}
